import { useEffect, useRef } from 'react';
import { offlineService } from '../../../services/supabaseService';
import {
  getCurrentBusinessId,
  businessDataStorageKey,
  normalizeBusinessStorageId
} from '../../../utils/unifiedDataHelpers';
import { logAchatsReload } from '../../../utils/achatReloadDebug';
import { shouldLoadLocalBusinessData } from '../../../utils/businessBootstrapUtils';
import { SYNC_BUSINESS_FIELDS_EVENT } from '../../../utils/businessStateSync';
import { withDeadline, warnLoad } from '../../../domain/persistence/businessLoadSupport';
import { isDeferPayloadConsolidationOnBootEnabled } from '../../../infrastructure/startup/bootFeatureFlags';
import { readStoredSessionUser, readStoredSessionBusinessType } from '../../../services/offlineAuthSession';
import {
  resolveAuthSessionKeyFromStorage,
  resolveSessionKeyFromUser
} from '../resolveAuthSession';
import {
  pageBoot,
  resetBusinessBootstrapSession,
  markBootstrapLoadSucceeded,
  normalizeBootSessionKey,
  getBootstrapGeneration
} from '../businessBootstrapSession';

export {
  resetBusinessBootstrapSession,
  prepareBootstrapForLogin,
  markBootstrapSoftLogout,
  isWarmBusinessMemory,
  getWarmBusinessSessionKey
} from '../businessBootstrapSession';

const BOOTSTRAP_RECOVERY_MS = 8000;
const BOOTSTRAP_RELINK_MS = 12000;

/**
 * Storage init, first load, auth/reload/sync window listeners.
 * - Cold start / first login: exactly one load
 * - Logout: clear session flags — next login always loads
 * - Business switch: one new load
 * - StrictMode remount: await same promise (no second load)
 */
export function useBusinessBootstrap({
  loadBusinessData,
  syncBusinessFieldsFromKvRef,
  businessIdRef,
  setCurrentBusinessId,
  setCurrentBusinessType,
  setDataLoaded,
  setDataShellReady,
  setReady,
  resetBusinessEntityState
}) {
  const resetBusinessEntityStateRef = useRef(resetBusinessEntityState);
  resetBusinessEntityStateRef.current = resetBusinessEntityState;

  useEffect(() => {
    let cancelled = false;
    /** Queued when auth fires while a boot load is in flight — flush after inFlight clears. */
    let pendingAuthCatchUp = null;

    const unlockBootstrapFlags = () => {
      setDataLoaded(true);
      setDataShellReady(true);
      setReady(true);
    };

    offlineService.loadQueue().catch((e) => {
      console.warn('[DataContext] offline queue load failed:', e?.message || e);
    });

    const flushPendingAuthCatchUp = () => {
      if (cancelled || !pendingAuthCatchUp) return;
      const user = pendingAuthCatchUp;
      pendingAuthCatchUp = null;
      logAchatsReload('auth.catchUp_after_inFlight', {
        businessId: resolveSessionKeyFromUser(user)
      });
      handleAuthStateChange({ detail: { user } });
    };

    const runBootstrapOnce = async () => {
      const sessionKey = resolveAuthSessionKeyFromStorage();
      if (!sessionKey) {
        if (!cancelled) {
          unlockBootstrapFlags();
        }
        // Mark idle (not a real business load) so login authStateChanged can load data
        resetBusinessBootstrapSession({ allowAuthLoad: true });
        return;
      }

      // New login session after logout (or different business)
      if (pageBoot.sessionKey && pageBoot.sessionKey !== sessionKey) {
        resetBusinessBootstrapSession();
      }

      if (pageBoot.done && pageBoot.sessionKey === sessionKey && pageBoot.promise) {
        try {
          await pageBoot.promise;
        } catch {
          /* ignore */
        }
        return;
      }
      if (pageBoot.promise && pageBoot.sessionKey === sessionKey) {
        try {
          await pageBoot.promise;
        } catch {
          /* ignore */
        }
        return;
      }

      pageBoot.inFlight = true;
      pageBoot.sessionKey = sessionKey;
      pageBoot.promise = (async () => {
        // Auth session only — never payload getUserInfo() (first-login empty gap)
        const user = readStoredSessionUser();
        if (!user) {
          unlockBootstrapFlags();
          return;
        }

        const businessType = readStoredSessionBusinessType();

        if (user.type === 'admin') {
          unlockBootstrapFlags();
          return;
        }

        const canonicalUserId = resolveSessionKeyFromUser(user) || null;
        const pinnedId = getCurrentBusinessId({ allowFallback: false });

        // Always prefer the logged-in account id (not a stale pin from a previous session)
        let businessId = canonicalUserId || pinnedId;
        if (canonicalUserId) {
          const { pinCurrentBusiness } = await import(
            '../../../infrastructure/storage/businessIdDiscovery'
          );
          pinCurrentBusiness(canonicalUserId);
          businessId = canonicalUserId;
          pageBoot.sessionKey = String(canonicalUserId);
        }

        if (!shouldLoadLocalBusinessData(businessId)) {
          unlockBootstrapFlags();
          return;
        }

        logAchatsReload('init.beforeLoad', {
          resolvedBusinessId: businessId,
          idFromUser: canonicalUserId,
          idFromStorage: getCurrentBusinessId({ allowFallback: false }),
          canonicalKey: businessDataStorageKey(businessId),
          legacyKey: `business_${businessId}_data`,
          preferredLoginId: canonicalUserId,
          hadStalePin: Boolean(pinnedId && canonicalUserId && pinnedId !== canonicalUserId)
        });

        setCurrentBusinessId(businessId);
        setCurrentBusinessType(businessType);
        businessIdRef.current = businessId;

        // Payload consolidation is heavy — defer when flag on so login shell unlocks first
        if (!isDeferPayloadConsolidationOnBootEnabled()) {
          try {
            const { recoverBusinessPayloadOnStartup } = await import(
              '../../../infrastructure/storage/businessPayloadRecovery'
            );
            const rec = await withDeadline(
              Promise.resolve().then(() => recoverBusinessPayloadOnStartup(businessId)),
              BOOTSTRAP_RECOVERY_MS,
              'bootstrap_payload_recovery'
            );
            if (rec?.migrated && process.env.NODE_ENV === 'development') {
              console.info('[businessLoad] Recovered business blob on load', rec);
            }
          } catch (recErr) {
            warnLoad('payload recovery skipped', recErr);
          }
        }

        // Heal BEFORE first load: hydrate / backup — never auto-relink (keeps login id sticky)
        const loadId = businessId;
        const fromKey = String(normalizeBusinessStorageId(businessId));
        if (pageBoot.emptyBucketRecoveredFor !== fromKey) {
          try {
            const { healEmptyBusinessAfterLogin } = await import(
              '../../../infrastructure/storage/storageHealOrchestrator'
            );
            const heal = await withDeadline(
              healEmptyBusinessAfterLogin(businessId, { allowRelink: false }),
              BOOTSTRAP_RELINK_MS,
              'bootstrap_heal_before_load'
            );
            pageBoot.emptyBucketRecoveredFor = fromKey;
            if (heal?.healed) {
              logAchatsReload('init.storageHealed', {
                businessId: loadId,
                steps: heal.steps?.map((s) => s.id)
              });
            }
          } catch (healErr) {
            warnLoad('pre-load heal skipped', healErr);
          }
        }

        const loadGen = getBootstrapGeneration();
        await loadBusinessData(loadId);
        markBootstrapLoadSucceeded(loadId, loadGen);
      })()
        .catch((error) => {
          console.warn('[DataContext] bootstrap failed:', error?.message || error);
          unlockBootstrapFlags();
        })
        .finally(() => {
          pageBoot.inFlight = false;
          pageBoot.done = true;
          flushPendingAuthCatchUp();
        });

      await pageBoot.promise;
    };

    const handleAuthStateChange = (event) => {
      const { user } = event.detail || {};

      // Logout: always clear session — next login reloads from storage
      if (!user) {
        pendingAuthCatchUp = null;
        resetBusinessBootstrapSession({ allowAuthLoad: true });
        businessIdRef.current = null;
        setCurrentBusinessId(null);
        setCurrentBusinessType(null);
        setDataLoaded(false);
        setDataShellReady(false);
        setReady(false);
        // The provider survives logout — wipe entities so nothing leaks into the
        // login screen or gets merged into the next business (RL01/RL02).
        resetBusinessEntityStateRef.current?.();
        void import('../../../domain/persistence/businessLoadPhases')
          .then(({ resetAppliedBusinessKey }) => resetAppliedBusinessKey?.())
          .catch(() => {});
        logAchatsReload('auth.stateChange.reset_session', {});
        return;
      }

      if (user.type === 'admin') return;

      // Queue catch-up while a real boot load is in flight (avoid missed login load)
      if (pageBoot.inFlight) {
        pendingAuthCatchUp = user;
        logAchatsReload('auth.stateChange.queued_boot', {
          businessId: resolveSessionKeyFromUser(user)
        });
        return;
      }

      let businessType = null;
      if (user.type === 'business' && user.business) {
        businessType = user.business.business_type;
      } else if (user.user?.user_metadata?.business_type) {
        businessType = user.user.user_metadata.business_type;
      } else if (user.user_metadata?.business_type) {
        businessType = user.user_metadata.business_type;
      }

      const canonicalFromUser = resolveSessionKeyFromUser(user) || null;
      const pinnedId = getCurrentBusinessId({ allowFallback: false });
      const businessId = canonicalFromUser || normalizeBusinessStorageId(pinnedId);

      if (!businessId || !shouldLoadLocalBusinessData(businessId)) {
        unlockBootstrapFlags();
        return;
      }

      if (canonicalFromUser) {
        void import('../../../infrastructure/storage/businessIdDiscovery')
          .then(({ pinCurrentBusiness }) => pinCurrentBusiness(canonicalFromUser))
          .catch(() => {});
      }

      const nextKey = normalizeBootSessionKey(businessId);
      const prev = businessIdRef.current != null ? String(businessIdRef.current) : '';

      // Duplicate auth events during an already-loaded session only.
      // Logout bumps generation + clears memoryHot — never soft-skip after logout
      // (that left dataLoaded=false and stuck ConditionalRoutes on path loading).
      if (pageBoot.memoryHot && pageBoot.sessionKey === nextKey) {
        logAchatsReload('auth.stateChange.skipped_already_loaded', { businessId: nextKey });
        return;
      }

      logAchatsReload('auth.stateChange', {
        businessId: nextKey,
        previousBusinessIdRef: prev,
        preferredLoginId: canonicalFromUser,
        hadStalePin: Boolean(pinnedId && canonicalFromUser && pinnedId !== canonicalFromUser),
        willLoadBecauseRefChanged: true,
        firstLoginAfterIdle: !pageBoot.sessionKey,
        businessSwitched: Boolean(pageBoot.sessionKey && pageBoot.sessionKey !== nextKey)
      });
      setCurrentBusinessId(businessId);
      setCurrentBusinessType(businessType);
      businessIdRef.current = businessId;
      pageBoot.sessionKey = nextKey;
      pageBoot.done = true;
      pageBoot.inFlight = true;
      pageBoot.memoryHot = false;
      const loadGen = getBootstrapGeneration();

      void (async () => {
        const loadId = businessId;
        const fromKey = String(normalizeBusinessStorageId(businessId));
        // Same heal as cold boot — hydrate only; relink is repair-UI opt-in
        if (pageBoot.emptyBucketRecoveredFor !== fromKey) {
          try {
            const { healEmptyBusinessAfterLogin } = await import(
              '../../../infrastructure/storage/storageHealOrchestrator'
            );
            const heal = await withDeadline(
              healEmptyBusinessAfterLogin(businessId, { allowRelink: false }),
              BOOTSTRAP_RELINK_MS,
              'auth_heal_before_load'
            );
            pageBoot.emptyBucketRecoveredFor = fromKey;
            if (heal?.healed) {
              logAchatsReload('auth.storageHealed', {
                businessId: loadId,
                steps: heal.steps?.map((s) => s.id)
              });
            }
          } catch (healErr) {
            warnLoad('auth heal skipped', healErr);
          }
        }

        await loadBusinessData(loadId);
        markBootstrapLoadSucceeded(loadId, loadGen);
      })()
        .catch((err) => {
          warnLoad('auth load failed', err);
          unlockBootstrapFlags();
        })
        .finally(() => {
          if (loadGen === getBootstrapGeneration()) {
            pageBoot.inFlight = false;
          }
          flushPendingAuthCatchUp();
        });
    };

    const handleReloadBusinessData = (event) => {
      if (pageBoot.inFlight || !pageBoot.done) {
        logAchatsReload('reloadBusinessData.skipped_boot', {});
        return;
      }
      const { businessId } = event.detail || event?.payload || {};
      const incoming = businessId != null ? String(businessId) : '';
      const current = businessIdRef.current != null ? String(businessIdRef.current) : '';
      const sameBusiness =
        incoming === current ||
        (incoming &&
          current &&
          normalizeBusinessStorageId(incoming) === normalizeBusinessStorageId(current));
      if (sameBusiness && (businessId || businessIdRef.current)) {
        const id = businessIdRef.current || businessId;
        pageBoot.memoryHot = false;
        pageBoot.inFlight = true;
        const loadGen = getBootstrapGeneration();
        void Promise.resolve(loadBusinessData(id))
          .then(() => {
            markBootstrapLoadSucceeded(id, loadGen);
          })
          .finally(() => {
            if (loadGen === getBootstrapGeneration()) {
              pageBoot.inFlight = false;
            }
            flushPendingAuthCatchUp();
          });
      }
    };

    // Field-level updates must NOT trigger a full boot reload (that made the UI feel broken/slow).
    // Full reload only when explicitly forced or when no changedFields are provided.
    const onDomainReload = (event) => {
      if (event?.detail?.type !== 'business_payload_updated') return;
      const payload = event.detail.payload || {};
      const fields = payload.changedFields;
      if (Array.isArray(fields) && fields.length > 0 && !payload.forceFullReload) {
        syncBusinessFieldsFromKvRef.current?.(payload.businessId, fields);
        return;
      }
      handleReloadBusinessData({ detail: payload });
    };

    const handleSyncBusinessFields = (event) => {
      syncBusinessFieldsFromKvRef.current?.(event.detail?.businessId, event.detail?.fields);
    };

    window.addEventListener('authStateChanged', handleAuthStateChange);
    window.addEventListener('elkheir:domain-event', onDomainReload);
    window.addEventListener(SYNC_BUSINESS_FIELDS_EVENT, handleSyncBusinessFields);

    // Catch-up: authStateChanged often fires before DataProvider mounts on first login.
    // After listeners are up, if KV has a session but no successful load yet, load now.
    void (async () => {
      await runBootstrapOnce();
      if (cancelled) return;
      if (pageBoot.memoryHot || pageBoot.inFlight) return;
      const user = readStoredSessionUser();
      if (!user || user.type === 'admin') return;
      const key = resolveSessionKeyFromUser(user);
      if (!key || !shouldLoadLocalBusinessData(key)) return;
      logAchatsReload('auth.catchUp_after_mount', { businessId: key });
      handleAuthStateChange({ detail: { user } });
    })();

    return () => {
      cancelled = true;
      window.removeEventListener('authStateChanged', handleAuthStateChange);
      window.removeEventListener('elkheir:domain-event', onDomainReload);
      window.removeEventListener(SYNC_BUSINESS_FIELDS_EVENT, handleSyncBusinessFields);
    };
  }, []);
}
